import { defineNuxtRouteMiddleware, navigateTo } from "nuxt/app";
import { RouteLocationNormalized, useRouter } from "vue-router";
import { FilterProcessingService } from "@app/routing/FilterProcessingService";
import { FilterType } from "@studyportals/omnisearch-interfaces";
import { useRollbar } from "@composables/useRollbar";
import { getPortalTypeFromEnvServer } from "@app/utils/get-portal-type-from-env.server";
import { getPortalPathFromType } from "@app/utils/portal-detector";
import { isCatchAllRoute } from "@app/utils/route-helpers";
import { ROUTE_PREFIX } from "@generator/config/route-prefix";
import { IExtendedWithPortalTypeFilterValue } from "@app/routing/mappers/IExtendedWithPortalTypeFilterValue";
import { SortMapper } from "@app/routing/mappers/SortMapper";
import { SearchType } from "@domain/enums/SearchType";

const filterProcessingService = FilterProcessingService.getInstance();
const filterTypes = Object.values(FilterType) as string[];
const sortMapper = SortMapper.getInstance();


const isExcludedForSearchType = (sortingValueId: string, searchType: SearchType): boolean => {
	const configuredSortValue: IExtendedWithPortalTypeFilterValue | undefined = sortMapper
		.getAllExtendedFilterValues()
		.find((value) => value.id === sortingValueId);

	return configuredSortValue?.excludedForSearchTypes?.includes(searchType) ?? false;
};

const cleanupSorting = async (to: RouteLocationNormalized): Promise<RouteLocationNormalized> => {
	const toSearchType = filterProcessingService.getSearchType(to);
	const sortingValues = await sortMapper.getExtendedFilterValuesFromRoute(to);
	const selectedSortingValueId = sortingValues[0]?.id;
	if (selectedSortingValueId && isExcludedForSearchType(selectedSortingValueId, toSearchType)) {
		const query = { ...to.query };
		delete query[FilterType.SORTING];
		return {
			...to,
			query
		};
	}

	return to;
}

export default defineNuxtRouteMiddleware(async (to: RouteLocationNormalized) => {
	// Capture router synchronously before any async operations
	const router = useRouter();
	const { logError } = useRollbar();

	try {
		const toCleaned = await cleanupSorting(to);

		if(import.meta.client) {
			if (router.resolve(toCleaned).fullPath === to.fullPath) return;
			return navigateTo(toCleaned);
		}

		if (isCatchAllRoute(toCleaned)) {
			const educationLevel = getPortalPathFromType(getPortalTypeFromEnvServer());
			if (educationLevel) {
				return navigateTo({ path: `${ROUTE_PREFIX}/search/${educationLevel}`, query: toCleaned.query }, { redirectCode: 301 });
			}
		}

		const portalType = getPortalTypeFromEnvServer();
		let filters = await filterProcessingService.processRoute(toCleaned);
		filters = filterProcessingService.ensureImplicitFilters(filters, portalType);

		const route = filterProcessingService.processFilters(router, filters, toCleaned);

		const nonFilterQueryKeys = Object.keys(toCleaned.query || {}).filter(q => !filterTypes.includes(q) && q !== 'mh');
		for(const key of nonFilterQueryKeys) {
			if(route.query === undefined) {
				route.query = {};
			}

			route.query[key] = toCleaned.query[key] as string;
		}

		if (router.resolve(route).fullPath === toCleaned.fullPath) return;

		return navigateTo(route);
	} catch (error) {
		logError('Failed to process route filters', error as Error);
	}
});
